import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  CraditService,
  SectionSaveResult,
  SectionSaveStatus,
} from '../../../app/components/credit/shared/services/cradit.service';

export interface DocumentBuilderSectionState {
  sectionId: string;
  status: SectionSaveStatus;
  initialized: boolean;
  valid: boolean;
  message?: string;
}

function idleState(sectionId: string): DocumentBuilderSectionState {
  return {
    sectionId,
    status: 'idle',
    initialized: false,
    valid: true,
  };
}

/**
 * Tracks per-section save status (idle/dirty/saving/saved/error) for one
 * Document Builder visit. Provided on the page component so a fresh instance
 * is created per navigation instead of leaking state across proposals.
 * `saved` is seeded from the backend on load (see applyPersistedSaves); the
 * dirty/saving/error states are live, per-visit states by nature.
 */
@Injectable()
export class DocumentBuilderStateService implements OnDestroy {
  private readonly statesSignal = signal<ReadonlyMap<string, DocumentBuilderSectionState>>(
    new Map(),
  );

  private readonly resultSubscription: Subscription;

  readonly savedCount = computed(
    () =>
      Array.from(this.statesSignal().values()).filter(
        (state) => state.status === 'saved',
      ).length,
  );

  readonly dirtySectionIds = computed(() =>
    Array.from(this.statesSignal().values())
      .filter((state) => state.status === 'dirty')
      .map((state) => state.sectionId),
  );

  readonly hasUnsavedChanges = computed(() => this.dirtySectionIds().length > 0);

  readonly savingSectionId = computed(
    () =>
      Array.from(this.statesSignal().values()).find(
        (state) => state.status === 'saving',
      )?.sectionId ?? null,
  );

  readonly isNavigationAllowed = computed(() => this.savingSectionId() === null);

  constructor(private readonly creditService: CraditService) {
    this.resultSubscription = this.creditService.sectionSaveResult$.subscribe(
      (result) => this.applyResult(result),
    );
  }

  ngOnDestroy(): void {
    this.resultSubscription.unsubscribe();
  }

  ensureTracked(sectionId: string): void {
    if (this.statesSignal().has(sectionId)) {
      return;
    }

    this.updateMap((next) => next.set(sectionId, idleState(sectionId)));
  }

  stateFor(sectionId: string): DocumentBuilderSectionState {
    return this.statesSignal().get(sectionId) ?? idleState(sectionId);
  }

  isDirty(sectionId: string): boolean {
    return this.stateFor(sectionId).status === 'dirty';
  }

  isSaved(sectionId: string): boolean {
    return this.stateFor(sectionId).status === 'saved';
  }

  markDirty(sectionId: string): void {
    const current = this.stateFor(sectionId);

    if (current.status === 'saving') {
      return;
    }

    this.updateMap((next) =>
      next.set(sectionId, {
        ...current,
        status: 'dirty',
        message: undefined,
      }),
    );
  }

  /**
   * Optimistically marks a section as saving the moment the parent dispatches
   * the trigger — children only need to report the terminal outcome
   * (saved / validation-error / api-error) via reportSectionSaveResult.
   */
  markSaving(sectionId: string): void {
    const current = this.stateFor(sectionId);

    this.updateMap((next) =>
      next.set(sectionId, {
        ...current,
        status: 'saving',
        message: undefined,
      }),
    );
  }

  /**
   * Marks sections whose data is already persisted on the backend as saved,
   * so status survives a reload. Only untouched (`idle`) sections are
   * upgraded — a response arriving late never overrides unsaved edits, an
   * in-flight save or an error the user produced during this visit.
   */
  applyPersistedSaves(sectionIds: Iterable<string>): void {
    const upgradable = Array.from(sectionIds).filter(
      (sectionId) => this.stateFor(sectionId).status === 'idle',
    );

    if (upgradable.length === 0) {
      return;
    }

    this.updateMap((next) => {
      for (const sectionId of upgradable) {
        next.set(sectionId, {
          ...this.stateFor(sectionId),
          status: 'saved',
          initialized: true,
          valid: true,
          message: undefined,
        });
      }
    });
  }

  /**
   * For sections with no Save of their own (the Documents tab: uploads save
   * themselves): saved while complete, back to idle once it no longer is.
   * Never touches a section that is saving or has an unsaved/failed state.
   */
  setCompletion(sectionId: string, complete: boolean): void {
    const current = this.stateFor(sectionId);

    if (complete && (current.status === 'idle' || current.status === 'dirty')) {
      this.updateMap((next) =>
        next.set(sectionId, {
          ...current,
          status: 'saved',
          initialized: true,
          valid: true,
          message: undefined,
        }),
      );
    } else if (!complete && current.status === 'saved') {
      this.updateMap((next) => next.set(sectionId, idleState(sectionId)));
    }
  }

  applyResult(result: SectionSaveResult): void {
    const current = this.stateFor(result.sectionId);

    this.updateMap((next) =>
      next.set(result.sectionId, {
        ...current,
        sectionId: result.sectionId,
        status: result.status,
        initialized: true,
        valid: result.status !== 'validation-error' && result.status !== 'api-error',
        message: result.message,
      }),
    );
  }

  private updateMap(
    mutate: (next: Map<string, DocumentBuilderSectionState>) => void,
  ): void {
    const next = new Map(this.statesSignal());
    mutate(next);
    this.statesSignal.set(next);
  }
}
